/**
 * Sellograph Composable for Nuxt 3/4 — GA4-compatible event tracking.
 *
 * All events follow the GA4 Enhanced E-Commerce standard:
 * https://developers.google.com/analytics/devguides/collection/ga4/reference/events
 *
 * Items use the GA4 item schema:
 *   item_id, item_name, item_brand, item_category, price, currency, quantity, index
 */

// ─── GA4 Item Schema ────────────────────────────────────────────────

export interface SellographItem {
  item_id: string;
  item_name?: string;
  item_brand?: string;
  item_category?: string;
  item_category2?: string;
  item_category3?: string;
  item_variant?: string;
  item_list_id?: string;
  item_list_name?: string;
  affiliation?: string;
  coupon?: string;
  discount?: number;
  price?: number;
  currency?: string;
  quantity?: number;
  index?: number;
}

// ─── Debug ──────────────────────────────────────────────────────────

function dbg(event: string, data?: any) {
  if (typeof window === 'undefined' || !(window as any).__sg_debug) return;
  console.log(
    `%c[Sellograph]%c ${event}`,
    'background:#6366f1;color:#fff;padding:1px 6px;border-radius:3px;font-weight:bold',
    'color:#6366f1;font-weight:bold',
    data ?? '',
  );
}

// ─── Composable ─────────────────────────────────────────────────────

export function useSellograph() {
  const sfq = () => (typeof window !== 'undefined' ? (window as any).sfq : null);

  /** view_item — Product detail page */
  function trackProductView(data: {
    items: SellographItem[];
    currency?: string;
    value?: number;
  }) {
    dbg('view_item', data);
    sfq()?.('event', 'view_item', {
      currency: data.currency,
      value: data.value ?? data.items[0]?.price,
      items: data.items,
    });
  }

  /** view_item_list — Category / listing page */
  function trackCategoryView(data: {
    item_list_id?: string;
    item_list_name?: string;
    items: SellographItem[];
  }) {
    dbg('view_item_list', {
      item_list_id: data.item_list_id,
      item_list_name: data.item_list_name,
      items_count: data.items.length,
      items: data.items,
    });
    sfq()?.('event', 'view_item_list', {
      item_list_id: data.item_list_id,
      item_list_name: data.item_list_name,
      items: data.items,
    });
  }

  /** select_item — Click on product in a list */
  function trackSelectItem(data: {
    item_list_id?: string;
    item_list_name?: string;
    items: SellographItem[];
  }) {
    dbg('select_item', data);
    sfq()?.('event', 'select_item', {
      item_list_id: data.item_list_id,
      item_list_name: data.item_list_name,
      items: data.items,
    });
  }

  /** add_to_cart */
  function trackAddToCart(data: {
    items: SellographItem[];
    currency?: string;
    value?: number;
  }) {
    dbg('add_to_cart', data);
    sfq()?.('event', 'add_to_cart', {
      currency: data.currency,
      value: data.value,
      items: data.items,
    });
  }

  /** remove_from_cart */
  function trackRemoveFromCart(data: {
    items: SellographItem[];
    currency?: string;
    value?: number;
  }) {
    dbg('remove_from_cart', data);
    sfq()?.('event', 'remove_from_cart', {
      currency: data.currency,
      value: data.value,
      items: data.items,
    });
  }

  /** view_cart — Cart page opened */
  function trackViewCart(data: {
    items: SellographItem[];
    currency?: string;
    value?: number;
  }) {
    dbg('view_cart', data);
    sfq()?.('event', 'view_cart', {
      currency: data.currency,
      value: data.value,
      items: data.items,
    });
  }

  /** begin_checkout — Checkout started */
  function trackBeginCheckout(data: {
    items: SellographItem[];
    currency?: string;
    value?: number;
    coupon?: string;
  }) {
    dbg('begin_checkout', data);
    sfq()?.('event', 'begin_checkout', {
      currency: data.currency,
      value: data.value,
      coupon: data.coupon,
      items: data.items,
    });
  }

  /** add_shipping_info — Shipping step in checkout */
  function trackAddShippingInfo(data: {
    items: SellographItem[];
    currency?: string;
    value?: number;
    shipping_tier?: string;
    coupon?: string;
  }) {
    dbg('add_shipping_info', data);
    sfq()?.('event', 'add_shipping_info', {
      currency: data.currency,
      value: data.value,
      shipping_tier: data.shipping_tier,
      coupon: data.coupon,
      items: data.items,
    });
  }

  /** add_payment_info — Payment step in checkout */
  function trackAddPaymentInfo(data: {
    items: SellographItem[];
    currency?: string;
    value?: number;
    payment_type?: string;
    coupon?: string;
  }) {
    dbg('add_payment_info', data);
    sfq()?.('event', 'add_payment_info', {
      currency: data.currency,
      value: data.value,
      payment_type: data.payment_type,
      coupon: data.coupon,
      items: data.items,
    });
  }

  /** purchase — Order completed */
  function trackPurchase(data: {
    transaction_id: string;
    items: SellographItem[];
    currency?: string;
    value?: number;
    tax?: number;
    shipping?: number;
    coupon?: string;
    affiliation?: string;
  }) {
    dbg('purchase', data);
    sfq()?.('event', 'purchase', {
      transaction_id: data.transaction_id,
      currency: data.currency,
      value: data.value,
      tax: data.tax,
      shipping: data.shipping,
      coupon: data.coupon,
      affiliation: data.affiliation,
      items: data.items,
    });
  }

  /** add_to_wishlist */
  function trackAddToWishlist(data: {
    items: SellographItem[];
    currency?: string;
    value?: number;
  }) {
    dbg('add_to_wishlist', data);
    sfq()?.('event', 'add_to_wishlist', {
      currency: data.currency,
      value: data.value ?? data.items[0]?.price,
      items: data.items,
    });
  }

  /** search */
  function trackSearch(data: {
    search_term: string;
    items?: SellographItem[];
  }) {
    dbg('search', data);
    sfq()?.('event', 'search', {
      search_term: data.search_term,
      items: data.items,
    });
  }

  /** generate_lead / form_submit — Newsletter, contact form */
  function trackFormSubmit(data: {
    formName: string;
    email?: string;
  }) {
    dbg('form_submit', data);
    sfq()?.('event', 'form_submit', {
      form_name: data.formName,
      email: data.email,
    });
  }

  /** identify — Customer login/registration */
  function identifyCustomer(email: string) {
    dbg('identify', email);
    sfq()?.('identify', { email });
  }

  return {
    // Product discovery
    trackProductView,      // view_item
    trackCategoryView,     // view_item_list
    trackSelectItem,       // select_item
    trackSearch,           // search
    trackAddToWishlist,    // add_to_wishlist

    // Cart
    trackAddToCart,        // add_to_cart
    trackRemoveFromCart,   // remove_from_cart
    trackViewCart,         // view_cart

    // Checkout funnel
    trackBeginCheckout,    // begin_checkout
    trackAddShippingInfo,  // add_shipping_info
    trackAddPaymentInfo,   // add_payment_info
    trackPurchase,         // purchase

    // Other
    trackFormSubmit,       // form_submit
    identifyCustomer,      // identify
  };
}
